export const getBadgeDimensions = (orientation, format) => {
  const isLandscape = orientation === 'landscape'

  const badgeWidth = isLandscape ? 4.0 : 3.0
  const badgeHeight = isLandscape ? 3.0 : 4.0

  const pageSize = Object.freeze({
    A4: 'a4',
    USLetter: 'letter'
  })

  return {
    isLandscape,
    startX: isLandscape ? (format === pageSize.A4 ? 0.05 : 0.25) : 0.85,
    startY: 1.0,
    spacingX: isLandscape ? 0 : 0.5,
    spacingY: isLandscape ? 0 : 0.5,
    badgeWidth,
    badgeHeight,
    badgePerRow: 2,
    badgePerColumn: isLandscape ? 3 : 2,
    qrWidth: 0.9,
    qrHeight: 0.9,
    logoWidth: badgeWidth - 0.15,
    logoHeight: 0.9
  }
}

export const adjustFontSize = (text, fontSize) => {
  if (text.length < 25) {
    return { fontSize: fontSize + 1 }
  } else if (text.length < 30) {
    return { fontSize: fontSize + 0.5 }
  } else if (text.length > 30 && text.length <= 34) {
    return { fontSize: fontSize - 1 }
  } else if (text.length > 34) {
    return { fontSize: fontSize - 1.8 }
  }
  return { fontSize }
}

export const adjustFontSizeForLocation = (text, fontSize) => {
  if (text.length < 20) {
    return { fontSize: fontSize + 1 }
  } else if (text.length >= 20) {
    return { fontSize: fontSize + 0.5 }
  } else if (text.length >= 25) {
    return { fontSize: fontSize }
  } else if (text.length >= 30) {
    return { fontSize: fontSize - 0.5 }
  }
  return { fontSize }
}

export const adjustFontSizeToFitWidth = (doc, text, startSize, maxWidth, minSize = 4) => {
  let fontSize = startSize
  doc.setFontSize(fontSize)
  while (fontSize > minSize && doc.getTextWidth(text) > maxWidth) {
    fontSize -= 0.5
    doc.setFontSize(fontSize)
  }
  return fontSize
}

// Last-resort guard for a single unbroken token (e.g. one very long name with no
// space) that splitTextToSize can't wrap and adjustFontSizeToFitWidth can't shrink
// past minSize — truncates with an ellipsis so the line never bleeds past maxWidth.
export const truncateTextToWidth = (doc, text, maxWidth) => {
  if (doc.getTextWidth(text) <= maxWidth) return text
  let truncated = text
  while (truncated.length > 1 && doc.getTextWidth(`${truncated}…`) > maxWidth) {
    truncated = truncated.slice(0, -1)
  }
  return `${truncated}…`
}

export const ptToPxNumber = (pt) => pt * 1.3333 // 1pt = 1.3333px

export const ptToPx = (pt) => `${ptToPxNumber(pt)}px`

// MeetSymple QR (MSQR) string format version.
const MSQR_PREFIX = `::MSQR::`

// Canonical field -> MSQR key map. Order defines the order of pairs in the string.
// `firstName`/`lastName` are always included; the rest are gated by fieldsAppliedInsideQRCode.
const MSQR_FIELD_KEYS = {
  firstName: 'FN',
  lastName: 'LN',
  credential: 'CR',
  position: 'POS',
  organization: 'ORG',
  city: 'CT',
  state: 'ST',
  country: 'CNT',
  email: 'EM',
  phone: 'PH'
}

// Fields always encoded regardless of the badge config's selected QR fields.
const MSQR_ALWAYS_INCLUDED = ['firstName', 'lastName']

// Escape reserved characters in a value. Order matters: backslash must be escaped
// first, or we'd double-escape the backslashes inserted for `;` and `:`.
const escapeMSQRValue = (value) =>
  String(value).replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/:/g, '\\:')

export const buildBadgeQrPlainText = (data, fieldsAppliedInsideQRCode) => {
  const fields = fieldsAppliedInsideQRCode || []

  const pairs = []
  for (const [field, key] of Object.entries(MSQR_FIELD_KEYS)) {
    if (!MSQR_ALWAYS_INCLUDED.includes(field) && !fields.includes(field)) continue
    const raw = data?.[field]
    if (raw === undefined || raw === null || raw === '') continue
    pairs.push(`${key}:${escapeMSQRValue(raw)}`)
  }

  return MSQR_PREFIX + pairs.join(';')
}

// Escapes special characters per vCard (RFC 6350) TEXT value rules.
const escapeVCardValue = (value) =>
  String(value)
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n')

export const buildBadgeQrVCard = (data, fieldsAppliedInsideQRCode) => {
  const fields = fieldsAppliedInsideQRCode || []
  const rawFirst = data?.firstName ?? ''
  const rawLast = data?.lastName ?? ''
  const suffix = fields.includes('credential') ? data?.credential ?? '' : ''

  const lines = ['BEGIN:VCARD', 'VERSION:3.0']

  if (rawFirst || rawLast || suffix) {
    lines.push(
      `N:${escapeVCardValue(rawLast)};${escapeVCardValue(rawFirst)};;;${escapeVCardValue(suffix)}`
    )
  }

  if (fields.includes('organization') && data?.organization) {
    lines.push(`ORG:${escapeVCardValue(data.organization)}`)
  }
  if (fields.includes('position') && data?.position) {
    lines.push(`TITLE:${escapeVCardValue(data.position)}`)
  }
  if (fields.includes('email') && data?.email) {
    lines.push(`EMAIL:${escapeVCardValue(data.email)}`)
  }
  if (fields.includes('phone') && data?.phone) {
    lines.push(`TEL:${escapeVCardValue(data.phone)}`)
  }

  const hasLocation = ['city', 'state', 'country'].some((f) => fields.includes(f))
  if (hasLocation) {
    const city = data?.city ?? ''
    const state = data?.state ?? ''
    const country = data?.country ?? ''
    lines.push(
      `ADR:;;;${escapeVCardValue(city)};${escapeVCardValue(state)};;${escapeVCardValue(country)}`
    )
  }

  lines.push('END:VCARD')
  return lines.join('\r\n')
}

export const buildBadgeQrContent = (data, fieldsAppliedInsideQRCode, _badgeNameFormat, qrFormat) =>
  qrFormat === 'vcard'
    ? buildBadgeQrVCard(data, fieldsAppliedInsideQRCode)
    : buildBadgeQrPlainText(data, fieldsAppliedInsideQRCode)

let measureCanvasCtx = null
const getMeasureCanvasCtx = () => {
  if (!measureCanvasCtx) {
    measureCanvasCtx = document.createElement('canvas').getContext('2d')
  }
  return measureCanvasCtx
}

// Canvas-measured analogue of adjustFontSizeToFitWidth, for HTML/CSS previews (px, not jsPDF pt/in)
export const adjustFontSizeToFitWidthPx = (
  text,
  startSizePx,
  maxWidthPx,
  { minSizePx = 8, fontWeight = 'normal', fontFamily = 'helvetica, arial, sans-serif' } = {}
) => {
  const ctx = getMeasureCanvasCtx()
  let fontSize = startSizePx
  ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`
  while (fontSize > minSizePx && ctx.measureText(text).width > maxWidthPx) {
    fontSize -= 1
    ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`
  }
  return fontSize
}

// Greedy word-wrap analogue of jsPDF's splitTextToSize, for HTML/CSS previews. Needed because
// adjustFontSizeToFitWidthPx stops at its font-size floor: past that point the only way to
// keep long text inside the card is to break it across lines, as the PDF does.
export const wrapTextToWidthPx = (
  text,
  fontSizePx,
  maxWidthPx,
  { fontWeight = 'normal', fontFamily = 'helvetica, arial, sans-serif' } = {}
) => {
  const ctx = getMeasureCanvasCtx()
  ctx.font = `${fontWeight} ${fontSizePx}px ${fontFamily}`
  if (ctx.measureText(text).width <= maxWidthPx) return [text]

  const words = String(text).split(/\s+/).filter(Boolean)
  const lines = []
  let current = ''
  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word
    if (current && ctx.measureText(candidate).width > maxWidthPx) {
      lines.push(current)
      current = word
    } else {
      current = candidate
    }
  }
  if (current) lines.push(current)
  return lines.length ? lines : [text]
}

// px counterpart of truncateTextToWidth — last-resort guard for a single unbroken token
// (e.g. one long name with no space) that wrapping can't break and the font-size floor
// can't shrink enough. Truncating with an ellipsis keeps the line inside the card instead
// of letting `overflow: hidden` clip it mid-word at both ends.
export const truncateTextToWidthPx = (
  text,
  fontSizePx,
  maxWidthPx,
  { fontWeight = 'normal', fontFamily = 'helvetica, arial, sans-serif' } = {}
) => {
  const ctx = getMeasureCanvasCtx()
  ctx.font = `${fontWeight} ${fontSizePx}px ${fontFamily}`
  if (ctx.measureText(text).width <= maxWidthPx) return text
  let truncated = text
  while (truncated.length > 1 && ctx.measureText(`${truncated}…`).width > maxWidthPx) {
    truncated = truncated.slice(0, -1)
  }
  return `${truncated}…`
}
